import type { ConfidenceLevel, DataConfidence } from '@ace-tracker/shared';

const LEVEL: Record<ConfidenceLevel, { label: string; icon: string; chip: string }> = {
  high: { label: 'High', icon: 'fa-solid fa-circle-check', chip: 'bg-ok-bg text-ok-ink' },
  medium: { label: 'Medium', icon: 'fa-solid fa-circle-exclamation', chip: 'bg-warn-bg text-warn-ink' },
  low: { label: 'Low', icon: 'fa-solid fa-triangle-exclamation', chip: 'bg-crit-bg text-crit-ink' },
};

function percentText(value: number | null): string {
  return value === null ? 'n/a' : `${Math.round(value)}%`;
}

function recencyText(hours: number | null): string {
  if (hours === null) return 'no data';
  if (hours < 1) return 'under 1 h old';
  if (hours < 48) return `${Math.round(hours)} h old`;
  return `${Math.round(hours / 24)} days old`;
}

function Measure({ label, value, level, title }: { label: string; value: string; level: ConfidenceLevel; title: string }) {
  const style = LEVEL[level];
  return (
    <li className="flex items-center gap-1.5 whitespace-nowrap" title={title}>
      <i aria-hidden="true" className={`${style.icon} text-[11px] ${level === 'high' ? 'text-success' : level === 'medium' ? 'text-warn-ink' : 'text-crit-ink'}`} />
      <span className="text-text-muted">{label}</span>
      <span className="font-medium text-text-default">{value}</span>
      <span className="sr-only">({style.label.toLowerCase()})</span>
    </li>
  );
}

/**
 * Always-visible strip on the Executive view saying how far the figures can
 * be trusted: an overall level plus the four measures behind it. Each measure
 * carries its own icon and value, so the meaning never rests on color.
 */
export function ConfidenceIndicator({ confidence, onOpenHealth }: { confidence: DataConfidence; onOpenHealth: () => void }) {
  const overall = LEVEL[confidence.level];
  return (
    <section
      aria-label="Data confidence"
      className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-card border border-border bg-surface px-4 py-2.5 text-xs"
    >
      <span className={`inline-flex items-center gap-1.5 rounded-pill px-3 py-1 font-medium ${overall.chip}`}>
        <i aria-hidden="true" className={overall.icon} />
        Data confidence: {overall.label}
      </span>
      <ul className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
        <Measure
          label="Coverage"
          value={percentText(confidence.coverage.percent)}
          level={confidence.coverage.level}
          title={`${confidence.coverage.reporting} of ${confidence.coverage.expected} reported in the last 48 hours, counted per device per AI tool among those seen in the last 30 days.`}
        />
        <Measure
          label="Recency"
          value={recencyText(confidence.recency.hours)}
          level={confidence.recency.level}
          title="Age of the latest AI tool telemetry in the selected range."
        />
        <Measure
          label="Cost on fallback rate"
          value={percentText(confidence.fallbackCost.percent)}
          level={confidence.fallbackCost.level}
          title="Share of token cost priced at the fallback rate because the model has no price in Settings."
        />
        <Measure
          label="Developers identified"
          value={percentText(confidence.identity.percent)}
          level={confidence.identity.level}
          title={`${confidence.identity.matched} of ${confidence.identity.active} active developers have a name or team in Settings.`}
        />
      </ul>
      <button
        type="button"
        onClick={onOpenHealth}
        className="ml-auto inline-flex min-h-[44px] items-center gap-1.5 font-medium text-primary-blue underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
      >
        Data health
        <i aria-hidden="true" className="fa-solid fa-arrow-right text-[10px]" />
      </button>
    </section>
  );
}
